import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit, Trash2, Eye, Plus, Search, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { fetchItems, fetchItemsCount } from '../services/api.ts';
import './Items.css';

interface Item {
  id: number;
  name: string;
  avater: string;
  price: number;
  regular_price: number;
  url: string;
  enable: boolean;
}

const Items: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  useEffect(() => {
    const loadItems = async () => {
      try {
        setLoading(true);
        const [itemsData, countData] = await Promise.all([
          fetchItems(0, 1000),
          fetchItemsCount()
        ]);
        setItems(itemsData);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load items');
        console.error('Error loading items:', err);
      } finally {
        setLoading(false);
      }
    };

    loadItems();
  }, []);

  // Filter and search logic
  const filteredItems = items.filter((item) => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      filterStatus === 'all' ||
      (filterStatus === 'active' && item.enable) ||
      (filterStatus === 'inactive' && !item.enable);

    return matchesSearch && matchesStatus;
  });

  // Pagination
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedItems = filteredItems.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const calculateDiscount = (regularPrice: number, price: number) => {
    if (regularPrice <= 0 || price >= regularPrice) return 0;
    return Math.round(((regularPrice - price) / regularPrice) * 100);
  };

  if (loading) {
    return (
      <div className="items-page">
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading items...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="items-page">
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="items-page">
      <div className="page-header">
        <h2>Items</h2>
        <div className="page-actions">
          <div className="search-box">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search items..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
          <button className="add-btn" onClick={() => navigate('/dashboard/items/new')}>
            <Plus size={18} />
            Add New Item
          </button>
        </div>
      </div>

      <div className="filter-section">
        <div className="filter-group">
          <label>Status:</label>
          <select value={filterStatus} onChange={(e) => {
            setFilterStatus(e.target.value as any);
            setCurrentPage(1);
          }}>
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Showing:</label>
          <span style={{ color: '#6366f1', fontWeight: 600 }}>
            {filteredItems.length} item{filteredItems.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {paginatedItems.length === 0 ? (
        <div className="empty-state">
          <p>No items found matching your criteria.</p>
        </div>
      ) : (
        <>
          <div className="items-grid">
            {paginatedItems.map((item) => {
              const discount = calculateDiscount(item.regular_price, item.price);
              return (
                <div key={item.id} className="item-card">
                  <div className="item-image-wrapper">
                    <img
                      src={item.avater || 'https://via.placeholder.com/320x200?text=No+Image'}
                      alt={item.name}
                      className="item-image"
                    />
                    {discount > 0 && (
                      <span className="discount-badge">-{discount}%</span>
                    )}
                    <span className={`item-status ${item.enable ? 'active' : 'inactive'}`}></span>
                  </div>
                  <div className="item-content">
                    <div className="item-header">
                      <h3 className="item-title">{item.name}</h3>
                      <p className="item-id">ID: {item.id}</p>
                    </div>
                    <div className="item-pricing">
                      <div className="price-row">
                        <span className="current-price">${item.price.toFixed(2)}</span>
                        {item.regular_price > item.price && (
                          <span className="regular-price">${item.regular_price.toFixed(2)}</span>
                        )}
                      </div>
                      {item.url && (
                        <a 
                          href={item.url} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="product-link"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink size={14} />
                          View Product
                        </a>
                      )}
                    </div>
                    <div className="item-meta">
                      <span className={`status-badge ${item.enable ? 'active' : 'inactive'}`}>
                        {item.enable ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="item-actions">
                      <button className="view-btn" title="View Details">
                        <Eye size={16} />
                        View
                      </button>
                      <button
                        className="edit-btn"
                        onClick={() => navigate(`/dashboard/items/edit/${item.id}`)}
                        title="Edit"
                      >
                        <Edit size={16} />
                        Edit
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={18} />
                Previous
              </button>

              <div className="pagination-info">
                Page {currentPage} of {totalPages}
              </div>

              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
                <ChevronRight size={18} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Items;
